import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InteractiveChart from './InteractiveChart';
import InteractiveTable from './InteractiveTable';
import { CollapsibleSection, Tabs } from './CollapsibleSection';
import { DataExplorer } from './DataExplorer';

// Component registry - maps component names to their React implementations
const COMPONENT_REGISTRY = {
  Chart: InteractiveChart,
  InteractiveChart,
  Table: InteractiveTable,
  InteractiveTable,
  DataTable: InteractiveTable,
  Collapse: CollapsibleSection,
  CollapsibleSection,
  Accordion: CollapsibleSection,
  Tabs,
  DataExplorer,
  Explorer: DataExplorer,
};

// Parse component blocks from content
// Supports two formats:
// 1. ```component:ComponentName {...props} ```
// 2. <ComponentName prop1={value} prop2={value} />
const parseComponentBlocks = (content) => {
  const parts = [];
  let lastIndex = 0;

  // Pattern for code block components: ```component:Type\n{...json}\n```
  const codeBlockPattern = /```component:(\w+)\n([\s\S]*?)```/g;

  // Pattern for JSX-like components: <ComponentName {...props} />
  const jsxPattern = /<(Chart|Table|DataTable|Collapse|Tabs|DataExplorer|InteractiveChart|InteractiveTable|CollapsibleSection|Explorer)(\s+[^>]*)?(?:\/>|>([\s\S]*?)<\/\1>)/g;

  // First, find all code block components
  let match;
  const allMatches = [];

  while ((match = codeBlockPattern.exec(content)) !== null) {
    allMatches.push({
      type: 'codeBlock',
      index: match.index,
      length: match[0].length,
      componentType: match[1],
      propsString: match[2],
    });
  }

  // Then find JSX-like components
  while ((match = jsxPattern.exec(content)) !== null) {
    allMatches.push({
      type: 'jsx',
      index: match.index,
      length: match[0].length,
      componentType: match[1],
      propsString: match[2] || '',
      children: match[3],
    });
  }

  // Sort by index
  allMatches.sort((a, b) => a.index - b.index);

  // Process matches
  allMatches.forEach((m) => {
    // Add markdown text before this match
    if (m.index > lastIndex) {
      const textBefore = content.slice(lastIndex, m.index);
      if (textBefore.trim()) {
        parts.push({ type: 'markdown', content: textBefore });
      }
    }

    // Parse props
    let props = {};
    try {
      if (m.type === 'codeBlock') {
        props = JSON.parse(m.propsString.trim());
      } else {
        // Parse JSX-like props
        props = parseJSXProps(m.propsString);
        if (m.children) {
          props.children = m.children;
        }
      }
    } catch (e) {
      console.warn('Failed to parse component props:', e);
    }

    parts.push({
      type: 'component',
      componentType: m.componentType,
      props,
    });

    lastIndex = m.index + m.length;
  });

  // Add remaining markdown
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex);
    if (remaining.trim()) {
      parts.push({ type: 'markdown', content: remaining });
    }
  }

  return parts.length > 0 ? parts : [{ type: 'markdown', content }];
};

// Parse JSX-like props string into object
const parseJSXProps = (propsString) => {
  const props = {};
  if (!propsString?.trim()) return props;

  // Match prop patterns: name={value} or name="value" or name={JSON}
  const propPattern = /(\w+)=(?:\{([^}]+)\}|"([^"]+)"|'([^']+)')/g;
  let match;

  while ((match = propPattern.exec(propsString)) !== null) {
    const key = match[1];
    const value = match[2] || match[3] || match[4];

    // Try to parse as JSON for objects/arrays/booleans/numbers
    try {
      props[key] = JSON.parse(value);
    } catch {
      // If not valid JSON, use as string
      props[key] = value;
    }
  }

  return props;
};

// Markdown components with enhanced styling
const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-gray-900 my-4">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-bold text-gray-900 my-3">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-semibold text-gray-800 my-2">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-base font-semibold text-gray-800 my-2">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="text-sm font-semibold text-gray-700 my-1">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="text-xs font-semibold text-gray-700 my-1">{children}</h6>
  ),
  p: ({ children }) => (
    <p className="my-2 text-gray-700 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-6 my-2 text-gray-700">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 my-2 text-gray-700">{children}</ol>
  ),
  li: ({ children }) => <li className="my-1">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote
      className="pl-4 my-3 italic text-gray-700 py-3 rounded-r"
      style={{ borderLeft: '4px solid #3D2969', backgroundColor: 'rgba(61, 41, 105, 0.06)' }}
    >
      {children}
    </blockquote>
  ),
  code: ({ inline, className, children }) => {
    if (inline) {
      return (
        <code className="bg-gray-100 text-indigo-600 px-1.5 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      );
    }
    return (
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-3">
        <code className={`${className} text-sm font-mono`}>{children}</code>
      </pre>
    );
  },
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-sm text-gray-700 border-t border-gray-100">
      {children}
    </td>
  ),
  hr: () => <hr className="my-6 border-gray-200" />,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-600 hover:text-indigo-800 underline decoration-indigo-300 underline-offset-2"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  img: ({ src, alt, ...props }) => (
    <figure className="my-4">
      <img
        src={src}
        alt={alt || 'Chart'}
        className="max-w-full h-auto rounded-lg shadow-md mx-auto"
        style={{ maxHeight: '500px' }}
        loading="lazy"
        {...props}
      />
      {alt && alt !== 'Chart' && (
        <figcaption className="text-center text-sm text-gray-500 mt-2">
          {alt}
        </figcaption>
      )}
    </figure>
  ),
};

// Stat card for key metrics
const StatCard = ({ label, value, change, changeType = 'neutral' }) => {
  const changeColors = {
    positive: '#16a34a',
    negative: '#dc2626',
    neutral: '#6b7280',
  };

  return (
    <div
      className="bg-white"
      style={{
        border: '1px solid #f3f4f6',
        borderRadius: 10,
        boxShadow: '0px 6px 29px rgba(61, 41, 105, 0.1), 0px 0.75px 3.63px rgba(61, 41, 105, 0.04)',
        padding: '16px 20px',
      }}
    >
      <div style={{ color: 'rgba(31, 36, 48, 0.6)', fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#1F2430', fontSize: 20, fontWeight: 700 }}>{value}</div>
      {change !== undefined && change !== null && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            fontWeight: 500,
            color: changeColors[changeType] || changeColors.neutral,
          }}
        >
          {changeType === 'positive' && '↑'}
          {changeType === 'negative' && '↓'}
          {change}
        </div>
      )}
    </div>
  );
};

// Stat Grid — responsive: 2-col mobile, up to 4-col desktop, outer container
const StatGrid = ({ stats = [] }) => (
  <div
    className="my-4"
    style={{
      border: '1px solid #f3f4f6',
      borderRadius: 10,
      boxShadow: '0px 6px 29px rgba(89, 108, 148, 0.07), 0px 0.75px 3.63px rgba(89, 108, 148, 0.04)',
      padding: 16,
      background: '#fff',
    }}
  >
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  </div>
);

// Add to registry
COMPONENT_REGISTRY.StatCard = StatCard;
COMPONENT_REGISTRY.StatGrid = StatGrid;
COMPONENT_REGISTRY.Stats = StatGrid;

// Main MDX Renderer component
const MDXRenderer = ({ content, sandboxMetadata }) => {
  // Inject sandbox metadata charts/tables as available data
  const enrichedContent = useMemo(() => {
    if (!sandboxMetadata) return content;

    // If there's chart data, we can reference it in components
    // This allows the LLM to generate component blocks that use the sandbox data
    return content;
  }, [content, sandboxMetadata]);

  // Parse content into parts
  const parts = useMemo(() => {
    return parseComponentBlocks(enrichedContent);
  }, [enrichedContent]);

  return (
    <div className="mdx-content">
      {parts.map((part, index) => {
        if (part.type === 'markdown') {
          return (
            <ReactMarkdown
              key={index}
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {part.content}
            </ReactMarkdown>
          );
        }

        if (part.type === 'component') {
          const Component = COMPONENT_REGISTRY[part.componentType];
          if (!Component) {
            console.warn(`Unknown component type: ${part.componentType}`);
            return (
              <div key={index} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg my-2">
                <span className="text-yellow-700">Unknown component: {part.componentType}</span>
              </div>
            );
          }

          // If sandbox metadata has relevant data, inject it
          let props = { ...part.props };
          if (sandboxMetadata) {
            // Auto-inject chart data if component references sandbox
            if ((part.componentType === 'Chart' || part.componentType === 'InteractiveChart') &&
                sandboxMetadata.plot_data?.length > 0 &&
                !props.data) {
              props.data = sandboxMetadata.plot_data[0];
            }
            // Auto-inject table data
            if ((part.componentType === 'Table' || part.componentType === 'InteractiveTable' || part.componentType === 'DataTable') &&
                sandboxMetadata.tables?.length > 0 &&
                !props.data) {
              props.data = sandboxMetadata.tables[0].data;
              props.columns = sandboxMetadata.tables[0].columns?.map(col => ({
                key: col,
                title: col,
                dataKey: col,
              }));
            }
          }

          return <Component key={index} {...props} />;
        }

        return null;
      })}
    </div>
  );
};

export default MDXRenderer;
export { COMPONENT_REGISTRY, parseComponentBlocks, StatCard, StatGrid };
